import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import LeafSignature from "@/components/heritage/LeafSignature";
import imgHair from "@/assets/yaphene-full-collection.jpg";
import imgBath from "@/assets/yaphene-soap-bars.jpg";
import imgPantry from "@/assets/flauriel-tea-collection.jpg";
import imgGlow from "@/assets/marapa-hyperpigmentation-set.jpg";
import imgTea from "@/assets/chlm-live-tea.jpg";
import imgGift from "@/assets/calume-gift-boxes.jpg";

type Collection = {
  title: string;
  whisper: string;
  image: string;
  to: string;
};

const COLLECTIONS: Collection[] = [
  { title: "Island Hair Rituals", whisper: "Scalp tonics, oils & the wash-day kit", image: imgHair, to: "/shop?category=hair" },
  { title: "Botanical Bathing Rituals", whisper: "Cold-process soaps, soaks & body bars", image: imgBath, to: "/shop?category=body" },
  { title: "Island Pantry Rituals", whisper: "Bush teas, tinctures, slow island food", image: imgPantry, to: "/shop?category=pantry" },
  { title: "Glow & Skin Rituals", whisper: "Marapa serums, creams & glow sets", image: imgGlow, to: "/brands/marapa" },
  { title: "Tea & Stillness Rituals", whisper: "Bush teas for morning, evening & reset", image: imgTea, to: "/caribbean-heritage-longevity-method" },
  { title: "Caribbean Gift Rituals", whisper: "Curated island gift boxes & keepsakes", image: imgGift, to: "/shop?category=gift-boxes" },
];

const PRIMARY = COLLECTIONS.slice(0, 3);
const SECONDARY = COLLECTIONS.slice(3);

const RitualCommerce = () => {
  return (
    <section className="relative overflow-hidden py-28 md:py-40 bg-[hsl(40_30%_97%)]">
      <LeafSignature variant="watermark" position="top-left" size="lg" tone="light" />
      <LeafSignature variant="watermark" position="bottom-right" size="xl" tone="light" />
      <div className="relative container mx-auto px-6" style={{ maxWidth: "1280px" }}>
        <Reveal className="max-w-2xl mb-20 md:mb-24">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
            Ritual Commerce
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] text-foreground leading-[1.05] mb-6">
            Shop by Ritual
          </h2>
          <p className="font-serif italic text-foreground/70 text-lg md:text-xl leading-relaxed">
            Lifestyle rituals, not bins of inventory — each gathers the
            objects of a single Caribbean practice.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {PRIMARY.map((c, idx) => (
            <Reveal key={c.title} delay={idx * 80}>
              <Link
                to={c.to}
                className="group relative block aspect-[4/5] overflow-hidden rounded-sm bg-[hsl(150_35%_8%)]"
              >
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1100ms] ease-out"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-[hsl(150_35%_8%)]/85 via-[hsl(150_35%_8%)]/10 to-transparent"
                />
                <div className="absolute inset-x-0 bottom-0 p-7 md:p-8">
                  <p className="font-serif italic text-accent/90 text-sm mb-2">— Island Ritual</p>
                  <h3 className="font-serif text-2xl md:text-[1.75rem] text-[hsl(40_33%_96%)] leading-tight mb-2 group-hover:text-accent transition-colors duration-500">
                    {c.title}
                  </h3>
                  <p className="font-sans text-[13px] text-white">
                    {c.whisper}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.32em] text-white group-hover:text-accent transition-colors">
                    Enter the Ritual →
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {SECONDARY.length > 0 && (
          <Reveal className="mt-20 md:mt-24 text-center">
            <div aria-hidden className="mx-auto mb-8 h-px w-12 bg-accent/60" />
            <p className="font-sans text-[11px] tracking-[0.36em] uppercase text-foreground/55 mb-5">
              Also within the house
            </p>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
              {SECONDARY.map((c) => (
                <Link
                  key={c.title}
                  to={c.to}
                  className="font-serif text-base md:text-lg text-foreground/75 hover:text-accent transition-colors"
                >
                  {c.title}
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default RitualCommerce;
